import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaShoppingCart,
  FaHeart,
} from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import api from "../api";

export default function ProductList() {
  const { category } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState(null);
  const [toast, setToast] = useState("");
  const [selectedSizes, setSelectedSizes] = useState({});

  const cream = "#F5EFE3";
  const teal = "#0F766E";

  const isAllProducts =
    decodeURIComponent(category || "")
      .trim()
      .toLowerCase() === "all";

  // =========================================================
  // TOAST
  // =========================================================

  const showToast = (message, duration = 2500) => {
    setToast(message);

    window.clearTimeout(showToast.timeout);

    showToast.timeout = window.setTimeout(() => {
      setToast("");
    }, duration);
  };

  // =========================================================
  // LOAD PRODUCTS
  // =========================================================

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      try {
        setLoading(true);

        const [productsRes, sectionsRes] =
          await Promise.all([
            api.get("/api/products/"),
            api.get("/api/sections/"),
          ]);

        const cleaned = decodeURIComponent(
          category || ""
        )
          .trim()
          .toLowerCase();

        const productsData = Array.isArray(
          productsRes.data
        )
          ? productsRes.data
          : Array.isArray(
              productsRes.data?.results
            )
          ? productsRes.data.results
          : [];

        const sectionsData = Array.isArray(
          sectionsRes.data
        )
          ? sectionsRes.data
          : Array.isArray(
              sectionsRes.data?.results
            )
          ? sectionsRes.data.results
          : [];

        if (!mounted) {
          return;
        }

        // =====================================================
        // ALL PRODUCTS
        // =====================================================

        if (cleaned === "all") {
          setCategoryData(null);
          setProducts(productsData);

          const initialSizes = {};

          productsData.forEach((product) => {
            if (
              Array.isArray(product.sizes) &&
              product.sizes.length
            ) {
              initialSizes[product.id] =
                product.sizes[0].id;
            }
          });

          setSelectedSizes(initialSizes);

          return;
        }

        // =====================================================
        // FIND CATEGORY SECTION
        // =====================================================

        const matched = sectionsData.find(
          (section) => {
            const sectionName =
              section.name
                ?.trim()
                .toLowerCase();

            const sectionCategory =
              section.filter_by?.Category ||
              section.filter_by?.category ||
              "";

            return (
              sectionName === cleaned ||
              String(sectionCategory)
                .trim()
                .toLowerCase() === cleaned
            );
          }
        );

        setCategoryData(
          matched || null
        );

        // =====================================================
        // FILTER PRODUCTS
        // =====================================================

        const filtered =
          productsData.filter(
            (product) => {
              if (
                typeof product.category ===
                "string"
              ) {
                return (
                  product.category
                    .trim()
                    .toLowerCase() ===
                  cleaned
                );
              }

              if (
                product.category &&
                typeof product.category ===
                  "object"
              ) {
                return (
                  product.category.name
                    ?.trim()
                    .toLowerCase() ===
                  cleaned
                );
              }

              return false;
            }
          );

        setProducts(filtered);

        // =====================================================
        // INITIAL SIZES
        // =====================================================

        const initialSizes = {};

        filtered.forEach((product) => {
          if (
            Array.isArray(product.sizes) &&
            product.sizes.length
          ) {
            initialSizes[product.id] =
              product.sizes[0].id;
          }
        });

        setSelectedSizes(initialSizes);
      } catch (error) {
        console.error(
          "Error loading products:",
          error
        );

        if (mounted) {
          setProducts([]);

          showToast(
            "Unable to load products. Please try again.",
            3000
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, [category]);

  // =========================================================
  // LOAD FAVORITES
  // =========================================================

  useEffect(() => {
    try {
      setFavorites(
        JSON.parse(
          localStorage.getItem("favs") ||
            "{}"
        )
      );
    } catch {
      setFavorites({});
    }
  }, []);

  // =========================================================
  // FAVORITE
  // =========================================================

  const toggleFavorite = async (
    product
  ) => {
    let localFavs = {};

    try {
      localFavs = JSON.parse(
        localStorage.getItem(
          "favs"
        ) || "{}"
      );
    } catch {
      localFavs = {};
    }

    const next =
      !localFavs[product.id];

    localFavs[product.id] = next;

    localStorage.setItem(
      "favs",
      JSON.stringify(localFavs)
    );

    setFavorites({
      ...localFavs,
    });

    const user = JSON.parse(
      localStorage.getItem(
        "user"
      ) || "{}"
    );

    if (!user?.access) {
      showToast(
        next
          ? "Added to favorites"
          : "Removed from favorites"
      );

      return;
    }

    try {
      await api.post(
        "/api/favorites/toggle/",
        {
          product_id:
            product.id,
        },
        {
          headers: {
            Authorization: `Bearer ${user.access}`,
          },
        }
      );

      showToast(
        next
          ? "Added to favorites"
          : "Removed from favorites"
      );
    } catch (error) {
      console.error(
        "Favorite error:",
        error
      );
    }
  };

  // =========================================================
  // SIZES
  // =========================================================

  const getSizes = (product) =>
    Array.isArray(product?.sizes)
      ? product.sizes
      : [];

  const getSelectedSize = (
    product
  ) => {
    const sizes =
      getSizes(product);

    if (!sizes.length) {
      return null;
    }

    return (
      sizes.find(
        (size) =>
          String(size.id) ===
          String(
            selectedSizes[
              product.id
            ]
          )
      ) || sizes[0]
    );
  };

  // =========================================================
  // PRODUCT IMAGE
  // ONLY FIRST IMAGE - NO SLIDER
  // =========================================================

  const getProductImage = (
    product
  ) => {
    if (product?.image) {
      return product.image;
    }

    if (
      Array.isArray(
        product?.sub_images
      ) &&
      product.sub_images.length
    ) {
      const firstImage =
        product.sub_images[0];

      if (
        typeof firstImage ===
        "string"
      ) {
        return firstImage;
      }

      if (firstImage?.image) {
        return firstImage.image;
      }
    }

    return "/placeholder.jpg";
  };

  // =========================================================
  // PRICE
  // =========================================================

  const getPriceData = (
    size
  ) => {
    if (!size) {
      return {
        price: null,
        discountPrice: null,
        discountPercent: 0,
        hasDiscount: false,
      };
    }

    const price = Number(
      size.price
    );

    if (!Number.isFinite(price)) {
      return {
        price: null,
        discountPrice: null,
        discountPercent: 0,
        hasDiscount: false,
      };
    }

    const rawDiscount =
      size.discount_price;

    const discountPrice =
      rawDiscount !== null &&
      rawDiscount !== undefined &&
      rawDiscount !== ""
        ? Number(rawDiscount)
        : null;

    if (
      Number.isFinite(
        discountPrice
      ) &&
      discountPrice < price
    ) {
      return {
        price,
        discountPrice,
        discountPercent:
          Math.round(
            ((price -
              discountPrice) /
              price) *
              100
          ),
        hasDiscount: true,
      };
    }

    return {
      price,
      discountPrice: null,
      discountPercent: 0,
      hasDiscount: false,
    };
  };

  // =========================================================
  // ADD TO CART
  // =========================================================

  const addToCart = async (
    product
  ) => {
    // SOLD PRODUCT
    if (product.is_sold === true) {
      showToast(
        "This product is sold."
      );

      return;
    }

    // LOGIN REQUIRED
    const user = JSON.parse(
      localStorage.getItem(
        "user"
      ) || "{}"
    );

    if (!user?.access) {
      showToast(
        "Please login first."
      );

      return;
    }

    const sizes =
      getSizes(product);

    const selectedSizeId =
      selectedSizes[
        product.id
      ];

    if (
      !selectedSizeId &&
      sizes.length
    ) {
      showToast(
        "Please select a size"
      );

      return;
    }

    const selectedSize =
      sizes.find(
        (size) =>
          String(size.id) ===
          String(
            selectedSizeId
          )
      );

    if (
      sizes.length &&
      !selectedSize
    ) {
      showToast(
        "Please select a valid size"
      );

      return;
    }

    try {
      await api.post(
        "/api/carts/add/",
        {
          product_id:
            product.id,
          size_id:
            selectedSize?.id ||
            null,
          quantity: 1,
        },
        {
          headers: {
            Authorization: `Bearer ${user.access}`,
          },
        }
      );

      window.dispatchEvent(
        new Event(
          "cartUpdated"
        )
      );

      showToast(
        "Added to cart"
      );
    } catch (error) {
      console.error(
        "Add to cart error:",
        error
      );

      showToast(
        error.response?.data
          ?.detail ||
          error.response?.data
            ?.error ||
          "Unable to add this product to cart."
      );
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] px-3 py-8 sm:px-4 sm:py-10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({
            length: 8,
          }).map((_, i) => (
            <div
              key={i}
              className="h-64 animate-pulse overflow-hidden rounded-2xl bg-[#F5EFE3] sm:h-80 md:h-96"
            />
          ))}
        </div>
      </div>
    );
  }

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div className="min-h-screen bg-[#FBF8F1]">

      {/* TOAST */}

      {toast && (
        <div className="fixed left-5 right-5 top-5 z-50 rounded-xl bg-[#0F766E] px-5 py-3 text-center font-medium text-white shadow-xl sm:left-auto">
          {toast}
        </div>
      )}

      {/* =====================================================
          CATEGORY HERO
      ===================================================== */}

      <section
        className={`relative flex min-h-[280px] items-center justify-center overflow-hidden text-center sm:min-h-[360px] lg:min-h-[440px] ${
          categoryData?.image
            ? "text-white"
            : "text-[#115E59]"
        }`}
        style={{
          backgroundImage:
            categoryData?.image
              ? `url(${categoryData.image})`
              : "none",

          backgroundColor:
            categoryData?.image
              ? "#173C39"
              : cream,

          backgroundSize:
            "cover",

          backgroundPosition:
            "center",
        }}
      >

        {/* DARK OVERLAY */}

        {categoryData?.image && (
          <div className="absolute inset-0 bg-black/50" />
        )}

        {/* BACK BUTTON */}

        <button
          type="button"
          onClick={() =>
            navigate("/")
          }
          aria-label="Back home"
          className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-md transition hover:bg-white/30 sm:left-6 sm:top-6"
        >
          <FaArrowLeft />
        </button>

        {/* HERO CONTENT */}

        <div className="relative z-10 max-w-4xl px-4">

          <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl">
            {isAllProducts
              ? "All Products"
              : categoryData?.name ||
                category}
          </h1>

          {categoryData?.description && (
            <p className="mt-4 text-sm leading-7 text-white/85 sm:text-base">
              {
                categoryData.description
              }
            </p>
          )}

          {isAllProducts && (
            <p
              className={`mt-4 text-sm sm:text-base ${
                categoryData?.image
                  ? "text-white/80"
                  : "text-slate-600"
              }`}
            >
              Explore our complete
              collection of
              handcrafted rugs.
            </p>
          )}

        </div>
      </section>

      {/* =====================================================
          PRODUCTS
      ===================================================== */}

      <section className="mx-auto max-w-7xl px-3 py-10 sm:px-4 md:px-6 lg:px-8 lg:py-14">

        {products.length === 0 ? (

          <div className="py-20 text-center text-lg text-slate-500">
            No products found
          </div>

        ) : (

          <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">

            {products.map(
              (product) => {

                const sizes =
                  getSizes(product);

                const selectedSize =
                  getSelectedSize(
                    product
                  );

                const priceData =
                  getPriceData(
                    selectedSize
                  );

                const image =
                  getProductImage(
                    product
                  );

                const isSold =
                  product.is_sold ===
                  true;

                return (

                  <article
                    key={product.id}
                    className="group overflow-hidden rounded-xl border border-[#E7DCC9] bg-[#F5EFE3] shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:rounded-2xl"
                  >

                    {/* =================================================
                        PRODUCT IMAGE
                        NO SLIDER
                        NO ARROWS
                        NO DOTS
                    ================================================= */}

                    <div
                      className="relative h-44 cursor-pointer overflow-hidden sm:h-56 md:h-64 lg:h-72"
                      onClick={() =>
                        navigate(
                          `/product/${product.id}`
                        )
                      }
                    >

                      <img
                        src={image}
                        alt={
                          product.name ||
                          "Handmade Afghan rug"
                        }
                        loading="lazy"
                        className={`h-full w-full object-cover transition-transform duration-700 ${
                          !isSold
                            ? "group-hover:scale-105"
                            : ""
                        }`}
                      />

                      {/* SOLD */}

                      {isSold && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                          <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-800">
                            SOLD
                          </span>
                        </div>
                      )}

                      {/* DISCOUNT */}

                      {priceData.hasDiscount &&
                        !isSold && (
                          <span className="absolute left-2 top-2 rounded-full bg-[#0F766E] px-2.5 py-1 text-[10px] font-bold text-white sm:left-3 sm:top-3 sm:text-xs">
                            {
                              priceData.discountPercent
                            }
                            % OFF
                          </span>
                        )}

                      {/* FAVORITE */}

                      <button
                        type="button"
                        aria-label={
                          favorites[
                            product.id
                          ]
                            ? "Remove from favorites"
                            : "Add to favorites"
                        }
                        onClick={(
                          event
                        ) => {
                          event.stopPropagation();

                          toggleFavorite(
                            product
                          );
                        }}
                        className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-md transition hover:scale-105 sm:right-3 sm:top-3 sm:h-10 sm:w-10"
                      >
                        {favorites[
                          product.id
                        ] ? (
                          <FaHeart className="text-red-500" />
                        ) : (
                          <FiHeart className="text-slate-700" />
                        )}
                      </button>

                    </div>

                    {/* =================================================
                        PRODUCT CONTENT
                    ================================================= */}

                    <div className="p-3 sm:p-4">

                      {/* NAME */}

                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/product/${product.id}`
                          )
                        }
                        className="block w-full text-left"
                      >
                        <h3 className="line-clamp-2 text-sm font-semibold text-[#115E59] sm:text-base">
                          {product.name}
                        </h3>
                      </button>

                      {/* PRODUCT ID */}

                      <p className="mt-1 truncate text-[10px] text-[#8B5E3C] sm:text-xs">
                        {product.product_id ||
                          `RUG-${product.id}`}
                      </p>

                      {/* SIZE */}

                      {sizes.length >
                        0 && (
                        <select
                          aria-label="Select rug size"
                          value={
                            selectedSizes[
                              product.id
                            ] || ""
                          }
                          disabled={isSold}
                          onClick={(
                            event
                          ) =>
                            event.stopPropagation()
                          }
                          onChange={(
                            event
                          ) =>
                            setSelectedSizes(
                              (
                                prev
                              ) => ({
                                ...prev,
                                [product.id]:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                          className="mt-3 w-full rounded-lg border border-[#D8CDBD] bg-white px-2 py-2 text-xs text-[#115E59] outline-none transition focus:ring-2 focus:ring-[#0F766E]/20 sm:text-sm"
                        >

                          <option value="">
                            Select size
                          </option>

                          {sizes.map(
                            (size) => {

                              const optionPrice =
                                getPriceData(
                                  size
                                );

                              return (
                                <option
                                  key={
                                    size.id
                                  }
                                  value={
                                    size.id
                                  }
                                >
                                  {
                                    size.length_ft ||
                                    size.length_cm
                                  }{" "}
                                  ×{" "}
                                  {
                                    size.width_ft ||
                                    size.width_cm
                                  }

                                  {optionPrice.discountPrice !=
                                    null
                                    ? ` — $${optionPrice.discountPrice.toFixed(
                                        2
                                      )}`
                                    : optionPrice.price !=
                                      null
                                    ? ` — $${optionPrice.price.toFixed(
                                        2
                                      )}`
                                    : ""}
                                </option>
                              );
                            }
                          )}

                        </select>
                      )}

                      {/* PRICE + CART */}

                      <div className="mt-3 flex items-center justify-between gap-2">

                        <div className="min-w-0">

                          {priceData.hasDiscount && (
                            <p className="text-[10px] text-slate-400 line-through sm:text-xs">
                              $
                              {priceData.price.toFixed(
                                2
                              )}
                            </p>
                          )}

                          <p className="text-base font-bold text-[#0F766E] sm:text-lg">
                            {priceData.price ==
                            null
                              ? "Price unavailable"
                              : `$${Number(
                                  priceData.discountPrice ??
                                    priceData.price
                                ).toFixed(
                                  2
                                )}`}
                          </p>

                        </div>

                        {/* CART BUTTON */}

                        <button
                          type="button"
                          disabled={
                            isSold ||
                            sizes.length ===
                              0 ||
                            !selectedSizes[
                              product.id
                            ]
                          }
                          onClick={() =>
                            addToCart(
                              product
                            )
                          }
                          aria-label={
                            isSold
                              ? "Sold"
                              : "Add to cart"
                          }
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F766E] text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:bg-[#0B5F59] hover:shadow-md disabled:cursor-not-allowed disabled:bg-[#D6D3D1] disabled:text-[#78716C] disabled:opacity-100"
                        >

                          {isSold ? (
                            <span className="text-[9px] font-bold">
                              SOLD
                            </span>
                          ) : (
                            <FaShoppingCart className="text-sm" />
                          )}

                        </button>

                      </div>

                    </div>

                  </article>
                );
              }
            )}

          </div>
        )}

      </section>
    </div>
  );
}